import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { Field, ErrorMessage } from '@atlaskit/form';
import Skeleton from '@atlaskit/skeleton';
import type { DocNode } from '@atlaskit/adf-schema';
import Textfield from '@atlaskit/textfield';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { LoadableAfterPaint } from '@confluence/loadable';

import { messages } from '../../messages';

const LoadingSkeleton = () => <Skeleton width="100%" height="16px" testId="skeleton" />;

const LazyEditorComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-EditorComponent" */ './editor/EditorComponent'))
			.EditorComponent,
	loading: LoadingSkeleton,
});

export type RichTextFieldProps = {
	value?: DocNode;
	label?: string;
	name?: string;
	testId?: string;
	onChange?: (value: DocNode) => void;
	isDisabled?: boolean;
	isRequired?: boolean;
	isLoading?: boolean;
	isCreatedWithAi?: boolean;
};

export const RichTextField = ({
	value,
	label,
	name = 'description',
	testId = 'work-create-form-description-field',
	onChange,
	isDisabled = false,
	isRequired = false,
	isLoading = false,
	isCreatedWithAi = false,
}: RichTextFieldProps) => {
	const { formatMessage } = useIntl();

	const defaultLabel = formatMessage(messages.workCreateCommonDescriptionLabel);

	useEffect(() => {
		if (value) {
			onChange?.(value);
		}
	}, [value, onChange]);

	const handleAnalyticsEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			// Add fieldName to editor analytics events
			event.update((payload) => ({
				...payload,
				attributes: {
					...payload.attributes,
					fieldName: `workCreateFormRichTextField-${name}`,
					isCreatedWithAi,
				},
			}));
		},
		[name, isCreatedWithAi],
	);

	return (
		<AnalyticsListener onEvent={handleAnalyticsEvent} channel="editor">
			<Field name={name} label={label || defaultLabel} testId={testId} isRequired={isRequired}>
				{({ fieldProps: { id }, error }) => (
					<>
						{!isLoading && value ? (
							<LazyEditorComponent
								id={id}
								initialValue={value}
								onChange={onChange}
								isDisabled={isDisabled || isLoading}
							/>
						) : (
							<Textfield
								placeholder={formatMessage(
									messages.workCreateCommonDescriptionAiLoadingPlaceholder,
								)}
								isDisabled
							/>
						)}
						{error && <ErrorMessage>{error}</ErrorMessage>}
					</>
				)}
			</Field>
		</AnalyticsListener>
	);
};
